export class TabItem extends HTMLElement {

    private owner: TabControl | undefined;
    readonly headerControl: HTMLButtonElement;

    constructor() {
        super();
        this.headerControl = document.createElement("button");
        this.headerControl.className = 'tab-header';
        this.headerControl.addEventListener("click", () => {
            const owner = this.getOwner();
            if (owner !== undefined) {
                owner.SelectedItem = this;
            }
        })
    }

    private getOwner(): TabControl | undefined {
        if (this.owner === undefined) {
            this.owner = this.closest('tab-control') as TabControl;
        }
        return this.owner;
    }
    connectedCallback(): void {
        this.owner = undefined;
    }
    disconnectedCallback(): void {
        this.owner = undefined;
    }
    ownerRefreshing(o: TabControl) {
        if (this.owner == o) this.owner = undefined;
    }
    informOwner(o: TabControl) {
        this.owner = o;
    }

    static get observedAttributes(): string[] {
        return ['header'];
    }

    get header(): string {
        return this.getAttribute('header') ?? '';
    }
    set header(value: string) {
        this.setAttribute('header', value);
    }

    informSelected(s: boolean) {
        if (s) {
            this.headerControl.classList.add('is-selected');
            this.classList.add('is-selected');
        } else {
            this.headerControl.classList.remove('is-selected');
            this.classList.remove('is-selected');
        }
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
        if (name == 'header') {
            this.headerControl.textContent = newValue;
        }
    }
}

export interface TabControlSelectionChangedDetail {
    previous: TabItem | undefined;
    current: TabItem;
}

declare global {
    interface HTMLElementEventMap {
        tabSelectionChanged: CustomEvent<TabControlSelectionChangedDetail>;
    }
}

export class TabControl extends HTMLElement {

    private initializedConnected: boolean = false;
    private readonly headerBar: HTMLDivElement;
    private itemsValid: boolean = false;
    private must_update_selection: boolean = true;
    private readonly items: TabItem[] = [];
    private selected_item: TabItem | undefined;
    private prevent_fire_event: boolean = false;

    constructor() {
        super();
        this.headerBar = document.createElement("div");
        this.headerBar.className = 'tab-headers';
    }

    public get Items(): TabItem[] { return this.getItems(); }

    public get SelectedItem(): TabItem | undefined {
        return this.selected_item;
    }
    public set SelectedItem(x: TabItem) {
        if (x != this.selected_item) {
            const items = this.getItems();
            let found = -1;
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (item == x) {
                    found = i;
                    break;
                }
            }
            const previous = this.selected_item;
            this.selected_item = x;
            if (found < 0) {
                this.must_update_selection = true;
                return;
            }
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                item.informSelected(item == x);
            }
            this.must_update_selection = false;

            if (!this.prevent_fire_event) {
                this.dispatchEvent(new CustomEvent<TabControlSelectionChangedDetail>('tabSelectionChanged', {
                    detail: { previous, current: this.selected_item },
                    //bubbles: true, seulement si on veut listen l'event plus haut dans l'arbre
                }));
            }
        }
    }
    public SelectItemWithoutTriggeringEvent(x: TabItem) {
        const remember_prevent = this.prevent_fire_event;
        this.prevent_fire_event = true;
        this.SelectedItem = x;
        this.prevent_fire_event = remember_prevent;
    }

    private getItems(): TabItem[] {
        if (!this.itemsValid) {
            this.itemsValid = true;
            this.must_update_selection = true;
            this.items.length = 0;
            this.querySelectorAll(':scope > tab-item').forEach(x => {
                this.items.push(x as TabItem);
            });
        }
        return this.items;
    }

    connectedCallback() {
        if (!this.initializedConnected) {
            this.initializedConnected = true;
            this.insertBefore(this.headerBar, this.firstChild);
            // Any later mutation of child <wpf-tab-item> elements re-syncs the UI.
            const observer = new MutationObserver(() => this.refresh());
            observer.observe(this, { childList: true });
        }
        this.refresh();
    }

    refresh() {
        for (let i = 0; i < this.items.length; i++) {
            this.items[i].ownerRefreshing(this);
        }
        this.itemsValid = false;
        const items = this.getItems();
        let selected_found = false;
        for (let i = 0; i < items.length; i++) {
            const x = items[i];
            x.informOwner(this);
            if (x == this.selected_item) {
                selected_found = true;
            }
        }
        if (this.selected_item === undefined || (!selected_found)) {
            if (items.length > 0) {
                this.prevent_fire_event = true;
                this.SelectedItem = items[0];
                this.prevent_fire_event = false;
            }
        } else if (this.must_update_selection) {
            this.must_update_selection = false;
            for (let i = 0; i < items.length; i++) {
                const x = items[i];
                x.informSelected(x == this.selected_item);
            }
        }
        while (this.headerBar.children.length > 0) {
            this.headerBar.removeChild(this.headerBar.children[this.headerBar.children.length - 1]);
        }
        for (let i = 0; i < items.length; i++) {
            this.headerBar.appendChild(items[i].headerControl);
        }
    }
}

export function initializeTabControl() {
    customElements.define('tab-item', TabItem);
    customElements.define('tab-control', TabControl);
}
